import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import useCourseCategories from 'PlatForms/Dashboards/ExpertDashboard/Hooks/useExpertCourseCategory';
import './ExpertSkills.css';

const ExpertSkills = () => {
    const {
        mainCategories,
        loading,
        loadingMore,
        error,
        selectedCategories,
        isSubmitting,
        submitError,
        submitSuccess,
        skillsLoading,
        pagination,
        hasMoreCategories,
        fetchMoreCategories,
        toggleCategorySelection,
        submitSelections,
        refetch,
    } = useCourseCategories();

    const [expandedDomains, setExpandedDomains] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('list'); // Default to list for single card view

    // Professional Icons with more refined choices
    const domainIcons = {
        'Software Creation': 'bi-code-square',
        'Data & Intelligence': 'bi-graph-up-arrow',
        'Infrastructure & Security': 'bi-shield-check',
        'Strategic Leadership': 'bi-diagram-3-fill',
        'Operational Excellence': 'bi-gear-fill',
        'Business Analysis': 'bi-clipboard-data-fill',
    };

    const getDomainIcon = (categoryName) => domainIcons[categoryName] || 'bi-layers';

    const getSubcategoryNames = (mainCategory) => {
        if (!mainCategory.subCategories || mainCategory.subCategories.length === 0) 
            return 'Specialized expertise domain';
        
        const names = mainCategory.subCategories.map(sub => sub.name);
        return names.length <= 3 ? names.join(', ') : `${names.slice(0, 3).join(', ')} +${names.length - 3} more`;
    };

    const toggleDomain = (domainId) => {
        setExpandedDomains(prev => ({ ...prev, [domainId]: !prev[domainId] }));
    };

    // Expertise levels with more professional terminology
    const selectedCount = selectedCategories.length;
    const minRequired = 1; 
    const maxAllowed = 15;

    const getExpertiseLevel = () => {
        if (selectedCount === 0) return { label: 'Not Defined', color: '#6c757d', percent: 0, icon: 'bi-question-circle' };
        if (selectedCount < 3) return { label: 'Specialist', color: '#C99211', percent: 25, icon: 'bi-star' };
        if (selectedCount < 7) return { label: 'Proficient', color: '#9A6B0E', percent: 60, icon: 'bi-star-fill' };
        if (selectedCount < 11) return { label: 'Expert', color: '#198754', percent: 85, icon: 'bi-award' };
        return { label: 'Master', color: '#8C6400', percent: 100, icon: 'bi-trophy-fill' };
    };

    const status = getExpertiseLevel();

    // Filter categories based on search term
    const filteredCategories = mainCategories.filter(category => {
        const matchesSearch = searchTerm === '' || 
            category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (category.subCategories && category.subCategories.some(sub => 
                sub.name.toLowerCase().includes(searchTerm.toLowerCase())
            ));
        
        return matchesSearch;
    });

    // Initialize expanded domains when data is loaded
    useEffect(() => {
        if (mainCategories.length > 0 && !loading && !skillsLoading) {
            const expanded = {};

            // If there are selected categories, expand domains that contain them
            if (selectedCategories.length > 0) {
                selectedCategories.forEach(selectedCat => {
                    const parentCategory = mainCategories.find(mc =>
                        mc.subCategories && mc.subCategories.some(sub => sub.id === selectedCat.id)
                    );
                    if (parentCategory) {
                        expanded[parentCategory.id] = true;
                    }
                });
            } else {
                // If no selections, expand the first domain
                expanded[mainCategories[0].id] = true;
            }

            setExpandedDomains(expanded);
        }
    }, [mainCategories, selectedCategories, loading, skillsLoading]);

    const handleSubmit = async () => {
        if (selectedCount < minRequired) {
            toast.warning(`Please select at least ${minRequired} skill to verify your expert profile.`);
            return;
        }
        await submitSelections();
    };

    if (loading || skillsLoading) {
        return (
            <div className="expert-loading-container">
                <div className="expert-loading-spinner">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <h4 className="mt-3">Loading Expert Skills Taxonomy</h4>
                    <p className="text-muted">Preparing your professional skill mapping interface...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="expert-error-container">
                <div className="alert alert-danger">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    <strong>Error:</strong> {error}
                </div>
                <button className="btn btn-primary mt-3" onClick={refetch}>
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="expert-skills-container">
            {/* Professional Header */}
         <div className="expert-header">
    <div className="container">  {/* Add this container div */}
        <div className="row align-items-center">
            <div className="col-md-8">
                <h1 className="expert-title">Professional Expertise Mapping</h1>
                <p className="expert-subtitle">
                    Define your core competencies and technical skills. This data will be used to match you with 
                    relevant mentorship opportunities and course authorship.
                </p>
            </div>
        </div>
    </div>
</div>

            {/* Success Message */}
            {submitSuccess && (
                <div className="container mt-3">
                    <div className="alert alert-success alert-dismissible fade show" role="alert">
                        <i className="bi bi-check-circle-fill me-2"></i>
                        <strong>Success!</strong> Your expert skills have been saved successfully.
                        You selected {selectedCount} skills.
                        <button type="button" className="btn-close" onClick={() => {/* Clear success */ }}></button>
                    </div>
                </div>
            )}

            {/* Error Message */}
            {submitError && (
                <div className="container mt-3">
                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        <strong>Error:</strong> {submitError}
                        <button type="button" className="btn-close" onClick={() => {/* Clear error */ }}></button>
                    </div>
                </div>
            )}

            <div className="container">
                <div className="row">
                    <div className="col-lg-8">
                        {/* Search and View Controls */}
                        <div className="expert-controls">
                            <div className="row align-items-center">
                                <div className="col-md-6">
                                    <div className="input-group">
                                        <span className="input-group-text">
                                            <i className="bi bi-search"></i>
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search skills or domains..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6 text-md-end mt-3 mt-md-0">
                                    <small className="text-muted">
                                        <i className="bi bi-info-circle me-1"></i>
                                        Showing {filteredCategories.length} of {mainCategories.length} domains
                                    </small>
                                </div>
                            </div>
                        </div>

                        {/* Skills Display - Single Card Per Row */}
                        {filteredCategories.length === 0 ? (
                            <div className="alert alert-info mt-3">
                                <i className="bi bi-info-circle me-2"></i>
                                No categories found matching your search.
                            </div>
                        ) : (
                            <div className="skills-grid">
                                {filteredCategories.map((mainCategory) => (
                                    <div className={`expert-card ${expandedDomains[mainCategory.id] ? 'expanded' : ''}`} key={mainCategory.id}>
                                        <div className="expert-card-header" onClick={() => toggleDomain(mainCategory.id)}>
                                            <div className="d-flex align-items-center">
                                                <div className="expert-icon-wrapper">
                                                    <i className={`bi ${getDomainIcon(mainCategory.name)}`}></i>
                                                </div>
                                                <div className="ms-3">
                                                    <h5 className="mb-1 fw-bold">{mainCategory.name}</h5>
                                                    <small className="text-muted">{getSubcategoryNames(mainCategory)}</small>
                                                </div>
                                            </div>
                                            <i className={`bi bi-chevron-${expandedDomains[mainCategory.id] ? 'up' : 'down'} ms-auto`}></i>
                                        </div>

                                        {expandedDomains[mainCategory.id] && (
                                            <div className="expert-card-body">
                                                <div className="d-flex flex-wrap gap-2">
                                                    {mainCategory.subCategories.map((sub) => {
                                                        const isSelected = selectedCategories.some(s => s.id === sub.id);
                                                        const isDisabled = selectedCount >= maxAllowed && !isSelected;
                                                        
                                                        return (
                                                            <button
                                                                key={sub.id}
                                                                className={`skill-pill ${isSelected ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
                                                                onClick={() => !isDisabled && toggleCategorySelection(sub)}
                                                                disabled={isDisabled}
                                                                title={isDisabled ? 'Maximum skills reached' : ''}
                                                            >
                                                                {sub.name}
                                                                {isSelected && <i className="bi bi-check2 ms-2"></i>}
                                                                {isDisabled && <i className="bi bi-lock ms-2"></i>}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        
                        {hasMoreCategories && (
                            <div className="text-center mt-4">
                                <button 
                                    className="btn btn-outline-primary"
                                    onClick={fetchMoreCategories}
                                    disabled={loadingMore}
                                >
                                    {loadingMore ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Loading...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-arrow-down-circle me-2"></i>
                                            Load More Domains
                                        </>
                                    )}
                                </button>
                                <p className="text-muted mt-2 small">
                                    Showing {mainCategories.length} of {pagination.totalCount} domains
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Sidebar Summary */}
                    <div className="col-lg-4">
                        <div className="summary-sidebar sticky-top">
                            <div className="summary-card">
                                <h6 className="text-uppercase fw-bold small text-muted mb-4">Expertise Breadth</h6>
                                
                                <div className="progress mb-3" style={{ height: '10px' }}>
                                    <div 
                                        className="progress-bar" 
                                        style={{ width: `${status.percent}%`, backgroundColor: status.color }}
                                    ></div>
                                </div>
                                <div className="d-flex justify-content-between mb-4">
                                    <span className="small">{selectedCount} Skills Identified</span>
                                    <span className="small fw-bold">{status.label}</span>
                                </div>

                                <div className="selected-skills-list">
                                    {selectedCategories.length === 0 ? (
                                        <div className="text-center py-4 text-muted">
                                            <i className="bi bi-journal-code fs-2 d-block mb-2"></i>
                                            <p className="small">No skills selected for verification</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="mb-2 small text-muted">
                                                Selected: {selectedCount}/{maxAllowed}
                                            </div>
                                            {selectedCategories.map(cat => (
                                                <div key={cat.id} className="selected-skill-item">
                                                    <span>{cat.name}</span>
                                                    <i className="bi bi-x-circle" onClick={() => toggleCategorySelection(cat)}></i>
                                                </div>
                                            ))}
                                        </>
                                    )}
                                </div>

                                <button 
                                    className="btn btn-primary w-100 py-3 mt-4 fw-bold"
                                    disabled={isSubmitting || selectedCount < minRequired}
                                    onClick={handleSubmit}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Validating Profile...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-check-circle me-2"></i>
                                            Finalize Expert Profile
                                        </>
                                    )}
                                </button>
                                
                           
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExpertSkills;